<template>
  <div class="addons-select" data-cy="addons-select-modal">
    <SDialog v-model:open="dialogVisible">
      <SDialogContent
        class="addons-select !w-[80%] !max-w-[80%] !min-w-[715px]"
        @open-auto-focus.prevent
        @pointer-down-outside.prevent
        @focus-outside.prevent
      >
        <SDialogHeader>
          <SDialogTitle>{{ modalTitle }}</SDialogTitle>
        </SDialogHeader>
        <button
          class="goback-btn"
          v-if="timeSlotsModalVisible"
          @click="setShowTimeSlotsModal(false)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M19 12H5"
              stroke="black"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M12 19L5 12L12 5"
              stroke="black"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <div class="main-pc-info grid grid-cols-[repeat(24,minmax(0,1fr))]">
          <div class="col-span-14">
            <div class="mb-4">
              <p class="pc-name">{{ modalHeaderTitle }}</p>
            </div>
          </div>
        </div>

        <div
          v-if="showAddonsDates"
          class="addons-dates-container flex items-center justify-center gap-3"
          data-cy="addons-dates-container"
        >
          <SButton
            v-for="(day, index) in getAddonCalendarDays()"
            :key="index"
            type="button"
            size="lg"
            data-cy="addon-date-button"
            :data-date="day.format('YYYY-MM-DD')"
            :variant="
              dayjs(selectedDate).isSame(day, 'day') ? 'default' : 'outline'
            "
            :class="[
              'shrink-0 w-[110px] h-16 flex-col gap-0.5 px-3 py-2',
              { relative: day.isSame(dayjs(), 'day') },
            ]"
            @click="selectDate(day.toDate())"
          >
            <CalendarCheckIcon
              v-if="day.isSame(dayjs(), 'day')"
              class="absolute top-1 right-1 text-sm"
            />
            <span
              class="text-[11px] font-semibold uppercase leading-none opacity-90"
            >
              {{ day.format("ddd") }}
            </span>
            <span class="text-[15px] font-bold capitalize leading-tight">
              {{ day.format("MMM D") }}
            </span>
          </SButton>
          <SPopover v-if="showDatePicker" v-slot="{ close }">
            <SPopoverTrigger as-child>
              <SButton
                type="button"
                size="lg"
                data-cy="addon-calendar-button"
                :variant="getIsSelectedDateHidden() ? 'default' : 'outline'"
                :class="[
                  'shrink-0 w-[110px] h-16 px-3 py-2',
                  getIsSelectedDateHidden()
                    ? 'relative flex-col gap-0.5'
                    : 'flex-row gap-1.5',
                ]"
              >
                <template v-if="getIsSelectedDateHidden()">
                  <CalendarIcon class="absolute top-1 right-1 text-sm" />
                  <span
                    class="text-[11px] font-semibold uppercase leading-none opacity-90"
                  >
                    {{ dayjs(selectedDate).format("ddd") }}
                  </span>
                  <span class="text-[15px] font-bold capitalize leading-tight">
                    {{ dayjs(selectedDate).format("MMM D") }}
                  </span>
                </template>
                <template v-else>
                  <CalendarIcon style="font-size: 18px" />
                  {{ $t("common.calendar") }}
                </template>
              </SButton>
            </SPopoverTrigger>
            <SPopoverContent class="w-auto p-0" align="start">
              <SCalendar
                :model-value="selectedDateValue"
                :is-date-unavailable="
                  (date) => isDateDisabled(date.toDate(getLocalTimeZone()))
                "
                initial-focus
                @update:model-value="
                  (value) => {
                    if (value) {
                      selectDate(value.toDate(getLocalTimeZone()));
                    }
                    close();
                  }
                "
              />
            </SPopoverContent>
          </SPopover>
        </div>
        <!-- LOADING -->
        <div v-if="addonsLoading" class="spinner-overlay">
          <div class="timeslots-loading-container">
            <LoaderCircleIcon class="s-icon-spin spinner" />
            <p class="timeslots-loading">
              {{ $t("addons.timeslots-loading") }}
            </p>
          </div>
        </div>
        <!-- ADDONS STEP -->
        <div
          v-else-if="
            !timeSlotsModalVisible &&
            mainProductCategory &&
            sortedAddons.length &&
            addonsData
          "
        >
          <div class="error-container">
            <p v-if="packageMaxQtyError">
              {{
                $t("error.max-quantity-allowed") +
                " " +
                String(packageQuantities.maxQuantity)
              }}
            </p>
            <p v-if="packageMinQtyError">
              {{
                $t("error.min-quantity-allowed") +
                " " +
                String(packageQuantities.minQuantity)
              }}
            </p>
          </div>
          <div class="price-select-wrapper">
            <template v-for="addon in sortedAddons" :key="addon.id">
              <SSeparator class="price-list-divider" />
              <div
                class="price-selector flex items-center"
                ref="addonsRef"
                data-cy="addon-price-item"
              >
                <div
                  class="grid w-full grid-cols-[repeat(24,minmax(0,1fr))]"
                  v-if="addonsData[addon.id]"
                >
                  <div class="price-name-img-wrapper col-span-8">
                    <img
                      :src="productPicture(addonsData[addon.id].pc)"
                      class="!h-[80px] !w-[80px] shrink-0 rounded object-cover"
                    />
                    <div class="price-name-wrapper">
                      <p class="price-description">
                        {{ addon.isMandatory ? $t("addons.mandatory") : "" }}
                      </p>
                      <p class="price-name">
                        {{
                          priceName(
                            addonsData[addon.id].pc,
                            addonsData[addon.id].price
                          )
                        }}
                      </p>
                      <div
                        v-if="addonsData[addon.id].pc.isRecurring"
                        class="timeslot-actions"
                      >
                        <div
                          v-if="
                            (addonsData[addon.id].timeslots || []).length ||
                            addonsData[addon.id].chosenTimeslot
                          "
                        >
                          <button
                            v-if="!addonsData[addon.id].chosenTimeslot"
                            class="choose-timeslot"
                            :ref="setShakeRef(`selectTimeSlot-${addon.id}`)"
                            @click="
                              setShowTimeSlotsModal(
                                true,
                                addon,
                                addonsData[addon.id].timeslots || []
                              )
                            "
                            data-cy="select-timeslot-button"
                          >
                            {{ $t("common.select-timeslot") }}
                          </button>
                          <button
                            v-else
                            class="chosen-timeslot"
                            @click="
                              setShowTimeSlotsModal(
                                true,
                                addon,
                                addonsData[addon.id].timeslots || []
                              )
                            "
                            data-cy="chosen-timeslot-button"
                          >
                            {{
                              formatDateTime(
                                addonsData[addon.id].chosenTimeslot!
                                  .startDateTime
                              )
                            }}
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              fill="currentColor"
                              class="chosen-timeslot-chevron"
                              viewBox="0 0 16 16"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708"
                              />
                            </svg>
                          </button>
                        </div>
                        <div v-else-if="!addonsData[addon.id].chosenTimeslot">
                          <p
                            class="no-timeslots"
                            :ref="setShakeRef(`noTimeSlot-${addon.id}`)"
                          >
                            {{ $t("common.no-timeslot") }}
                          </p>
                        </div>
                      </div>
                      <div v-else>
                        <div
                          v-if="
                            addonsData[addon.id].pc.type !==
                              PCTypes[PCTypes.Retail] &&
                            !(addonsData[addon.id].timeslots || []).length
                          "
                        >
                          <p
                            class="no-timeslots"
                            :ref="setShakeRef(`noTimeSlot-${addon.id}`)"
                          >
                            {{ $t("common.no-timeslot") }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-span-9"></div>
                  <div
                    class="col-span-6 flex flex-col items-center justify-center"
                  >
                    <div class="flex items-center justify-center">
                      <SButton
                        type="button"
                        size="lg"
                        class="w-full price-button"
                        :class="{
                          transparent: !canDecrease(
                            addon,
                            addonsData[addon.id].pc,
                            addonsData[addon.id].price.uuid
                          ).res,
                        }"
                        @click="
                          decrease(
                            addon,
                            addonsData[addon.id].pc,
                            addonsData[addon.id].price.uuid
                          )
                        "
                      >
                        -
                      </SButton>
                      <input
                        style="position: relative"
                        type="number"
                        class="price-count flex items-center justify-center"
                        v-model.number="
                          pricesCount[addonsData[addon.id].price.uuid]
                        "
                        :disabled="
                          addonsData[addon.id].pc.isRecurring &&
                          !addonsData[addon.id].chosenTimeslot
                        "
                        data-cy="addon-price-quantity-input"
                      />
                      <div>
                        <SButton
                          type="button"
                          size="lg"
                          class="w-full price-button"
                          :class="{
                            transparent: !canIncrease(
                              addon,
                              addonsData[addon.id].pc,
                              addonsData[addon.id].price.uuid
                            ).res,
                          }"
                          @click="
                            increase(
                              addon,
                              addonsData[addon.id].pc,
                              addonsData[addon.id].price.uuid
                            )
                          "
                        >
                          +
                        </SButton>
                      </div>
                    </div>
                    <label
                      class="input-error"
                      v-if="
                        areObjectsEqual(
                          priceMaxQuantityLimit(
                            addon,
                            addonsData[addon.id].pc,
                            addonsData[addon.id].price.uuid,
                            PriceMaxQtyConstraint.EXCEEDED
                          ),
                          {
                            maxQtyLimit: true,
                            reason: PriceQtyLimitReason.ADDON,
                          }
                        )
                      "
                      >{{
                        $t("error.max-quantity-allowed") +
                        " " +
                        String(addon.maxQuantity)
                      }}</label
                    >
                    <label
                      class="input-error"
                      v-if="
                        priceMinQuantityLimit(
                          addon,
                          addonsData[addon.id].pc,
                          addonsData[addon.id].price.uuid
                        )
                      "
                      >{{
                        $t("error.min-quantity-allowed") +
                        " " +
                        String(addon.minQuantity)
                      }}</label
                    >
                    <label
                      class="input-error"
                      v-if="
                        addon.isMandatory &&
                        addonsData[addon.id].pc.isRecurring &&
                        !addonsData[addon.id].chosenTimeslot
                      "
                      >{{ $t("addons.select-timeslot") }}</label
                    >
                    <label class="price-value">
                      {{ addonsData[addon.id].pc.product.currency }}
                      {{ ((addon.displayedValue || 0) / 100).toFixed(2) }}
                    </label>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
        <!-- TIMESLOTS STEP -->
        <div
          class="addons-timeslot-select price-select-wrapper"
          v-if="timeSlotsModalVisible && timeSlotsModalData"
        >
          <SSeparator class="price-list-divider" />
          <div v-if="Object.keys(timeSlotsModalData.groupedTimeSlots).length">
            <div
              class="select-timeslots-wrapper"
              v-for="(
                groupedHours, day, index
              ) in timeSlotsModalData.groupedTimeSlots"
              :key="index"
            >
              <div
                v-for="(timeslots, hour, index) in groupedHours"
                :key="index"
                class="grouped-hours"
              >
                <p class="key-grouping" data-cy="timeslot-hour">{{ hour }}</p>
                <ul class="timeslots-list">
                  <li
                    v-for="timeslot in timeslots"
                    :key="timeslot.id"
                    @click="
                      chooseTimeSlot({
                        addonId: timeSlotsModalData.addon.id,
                        timeslot: timeslot,
                      })
                    "
                    data-cy="timeslot-item"
                  >
                    <div class="timeslot">
                      <p class="timeslot-start">
                        {{ dayjs(timeslot.startDateTime).format("HH:mm") }}
                      </p>
                      <div class="timeslot-price-seats">
                        <p class="seats">
                          {{ timeslot.availability }}
                          {{ $t("field.tickets-left") }}
                        </p>
                        <p class="price">
                          {{
                            (
                              (timeSlotsModalData.addon.displayedValue || 0) /
                              100
                            ).toFixed(2)
                          }}
                          {{ currency }}
                        </p>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div
            v-else
            style="
              display: flex;
              justify-content: center;
              align-items: center;
              color: gray;
              height: 50vh;
            "
          >
            <p style="font-size: 3rem">{{ $t("common.no-timeslot") }}</p>
          </div>
        </div>
        <SDialogFooter class="gap-3">
          <SButton
            type="button"
            variant="outline"
            class="min-w-[120px]"
            @click="hideDialog"
          >
            {{ $t("button.cancel") }}
          </SButton>
          <SButton
            type="button"
            class="min-w-[120px]"
            :loading="isLoading"
            :disabled="isDisabled"
            data-cy="confirm-button"
            @click="addToBasketWithAddons"
          >
            {{ $t("button.confirm") }}
          </SButton>
        </SDialogFooter>

        <!-- </div> -->
      </SDialogContent>
    </SDialog>
    <!-- <time-slots
      v-if="timeSlotsModalVisible && timeSlotsModalData"
      :timeslots-data="timeSlotsModalData"
      v-on:time-slot-chosen="chooseTimeSlot"
      v-on:close-time-slots-modal="setShowTimeSlotsModal(false)"
    /> -->
  </div>
</template>

<script lang="ts">
import { computed, ref, defineComponent, onMounted, PropType } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { debounce } from "lodash";
import dayjs, { Dayjs } from "dayjs";
import { useOrderStore } from "@smeetz/order-store";
import { Currency, LineType, OrderSessionState } from "@smeetz/order-common";
import ModalModule from "@/store/modules/modal";
import shoppingIcon from "@/assets/images/icons/shopping-icon.png";
import {
  IPackagePrice,
  IProductCategory,
  IProductSlotPrice,
  ISmeetzPicture,
  PCTypes,
  RetailOptionType,
} from "@/types/db/product-category";
import { ITimeSlot } from "@/types/db/timeslots";
import { ICreateAddons } from "@/types/db/product-slot-state";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { formatDateTime } from "@/utils/helpers/dates";
import { getLanguage } from "@/utils/helpers/lang";
import { createImageproxyUrl } from "@/utils/helpers/pictures";
import { sellOnSoldOut } from "@/utils/helpers/product-category";
import { getPriceNonAutomaticAddons } from "@/utils/vue-helpers/product-slot-state";
import {
  fetchAllPCsTimeslotsByTimeRange,
  getFutureTimeSlotsByProductCategory2,
} from "@/utils/helpers/timeslots";
import { areObjectsEqual } from "@/utils/helpers/common";
import { addToBasket } from "@/utils/vue-helpers/product-category";
import {
  AddonsModalProps,
  ModalComponents,
  ModalStatus,
} from "@/constants/modal";
import {
  PCSELECT_IMAGE_HEIGHT,
  PCSELECT_IMAGE_MEASUREMENT_UNIT,
  PCSELECT_IMAGE_WIDTH,
} from "@/constants/style";
import { getProductCategoryByPriceId } from "@/dexie/product-category";
import { getGiftCardAddonTimeslot } from "@/utils/vue-helpers/product-slot-state";
import {
  getTimeslotById,
  queryTimeslotsByProductCategories,
} from "@/dexie/timeslots";
import { LogLevel, LogService } from "@/services/logging";
import NotificationService from "@/services/notification.service";
import {
  SButton,
  SCalendar,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
  SPopover,
  SPopoverContent,
  SPopoverTrigger,
  SSeparator,
} from "@smeetz/ui-core";
import { getLocalTimeZone, parseDate } from "@internationalized/date";
import {
  CalendarIcon,
  CalendarCheckIcon,
  LoaderCircleIcon,
} from "@smeetz/ui-icons";

export default defineComponent({
  name: ModalComponents.Addons,
  components: {
    SButton,
    SCalendar,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SDialogHeader,
    SDialogTitle,
    SPopover,
    SPopoverContent,
    SPopoverTrigger,
    SSeparator,
    CalendarIcon,
    CalendarCheckIcon,
    LoaderCircleIcon,
  },
  props: {
    modalProps: {
      type: Object as PropType<{
        referrer?: ModalComponents;
        goBack?: boolean;
        props?: AddonsModalProps;
      }>,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n({});
    const orderStore = useOrderStore();
    const { selectedOrder, selectedOrderSummary: orderSummary } =
      storeToRefs(orderStore);

    // Snapshot of order lines taken at modal-open time. getAddons() uses it
    // as the baseline for addon counts so mutations to the order during the
    // modal's lifetime don't shift the displayed quantities.
    const initialOrderLines = orderSummary.value?.orderLines
      ? [...orderSummary.value.orderLines]
      : [];

    const dialogVisible = dialogVisibleForStatus(ModalStatus.Addons);
    const mainProductCategory = ref<IProductCategory | null>(null);
    const currentLanguage = ref(getLanguage());
    const mainTicketTimeSlot = ref<ITimeSlot | null>(null);
    const addons = ref<IPackagePrice[]>([]);
    const addonsData = ref<
      Record<
        number,
        {
          pc: IProductCategory;
          price: IProductSlotPrice;
          timeslots?: ITimeSlot[];
          chosenTimeslot?: ITimeSlot;
          addonBeforeOffset?: number;
          addonAfterOffset?: number;
        }
      >
    >({});
    // Addons sorted by `priority` ascending (lower number = higher priority,
    // matching how they're configured in backoffice). Sort is stable, so equal
    // priorities keep their backend/insertion order as tie-breaker.
    const sortedAddons = computed(() =>
      [...addons.value].sort((a, b) => a.priority - b.priority)
    );
    const pricesCount = ref<Record<string, number>>({});
    const pricesBookingCount = ref<Record<string, number>>({});
    const isLoading = ref(false);
    const timeSlotsModalVisible = ref(false);
    const timeSlotsModalData = ref<{
      addon: IPackagePrice;
      groupedTimeSlots: Record<string, Record<string, ITimeSlot[]>>;
    } | null>(null);

    const selectedDate = ref<Date>(new Date());

    // Stable ref so the picker doesn't reset its displayed month on every render (L3-7935).
    const selectedDateValue = computed(() =>
      selectedDate.value
        ? parseDate(dayjs(selectedDate.value).format("YYYY-MM-DD"))
        : undefined
    );

    const addonsLoading = ref(false);

    const maxDaysBeforeMainTicket = ref(0);
    const maxDaysAfterMainTicket = ref(0);

    const showDatePicker = ref(false);

    enum PriceMaxQtyConstraint {
      "REACHED",
      "EXCEEDED",
    }
    enum PriceQtyLimitReason {
      "PACKAGE", // whole package has a quantity constraint
      "ADDON", // only the addon has a quantity constraint
    }

    const isMainPcRetail = computed(() => {
      return mainProductCategory.value?.type === PCTypes[PCTypes.Retail];
    });

    const productCategoryName = computed(() => {
      return (
        mainProductCategory.value?.nameLocale[currentLanguage.value] ||
        mainProductCategory.value?.name
      );
    });

    const pcselectPictureSize = computed(() => {
      return {
        width: PCSELECT_IMAGE_WIDTH + PCSELECT_IMAGE_MEASUREMENT_UNIT,
        height: PCSELECT_IMAGE_HEIGHT + PCSELECT_IMAGE_MEASUREMENT_UNIT,
      };
    });

    // TODO (perf): this validator is O(n²) over addons because the inner
    // `addons.value.find(...)` re-scans the full list each iteration, and
    // similar full-list scans run inside `packageMaxQtyError`,
    // `packageMinQtyError`, and `priceMaxQuantityLimit`. Once the addon
    // selection flow is stable, lift two memoized maps to setup scope —
    //   addonById: Map<number, IPackagePrice>
    //   priceCountSumByPriceUuid: Map<string, number>
    // — and replace the inner `find` + per-addon order-line filters with
    // O(1) lookups. Touch carefully: `isDisabled` gates the Confirm button.
    const isDisabled = computed(() => {
      if (!addonsData.value) {
        return true;
      }
      const addonsIds = Object.keys(addonsData.value);
      for (let i = 0; i < addonsIds.length; i++) {
        const addonId = Number(addonsIds[i]);
        const addon = addons.value.find((addon) => addon.id === addonId);
        if (!addon) {
          continue;
        }
        const addonPC: IProductCategory = addonsData.value[addonId].pc;
        const isRetail = addonPC.type === PCTypes[PCTypes.Retail];
        const isGiftCard = addonPC.type === PCTypes[PCTypes.GiftCard];
        const price = addonsData.value[addonId].price;
        const priceCount = pricesCount.value[price.uuid] ?? 0;
        // TODO: should we really filter by the price only?
        const priceCountConfirmedInBooking = orderSummary.value?.orderLines
          .filter(
            (line) =>
              line.parentLineId &&
              line.priceId === price.uuid &&
              line.state === OrderSessionState.COMPLETED
          )
          .reduce((acc, line) => acc + line.quantity, 0);

        // if data isn't initialized
        if (!addonPC || !addonPC.prices || !addonPC.prices.length) {
          return true;
        }
        // if mandatory addons weren't selected or don't meet minimum quantity requirement
        else if (
          addon.isMandatory &&
          (priceCount === 0 || priceCount < addon.minQuantity)
        ) {
          return true;
        }
        // for recurring tickets
        else if (addonPC.isRecurring) {
          // if no time slot was chosen
          if (addon.isMandatory && !addonsData.value[addonId].chosenTimeslot) {
            return true;
          }
          // if quantity was increased but no timeslot chosen
          if (!addonsData.value[addonId].chosenTimeslot && priceCount > 0) {
            return true;
          }
          // if a time slot was chosen, quantity increased but minimum quantity wasn't met
          if (
            addonsData.value[addonId].chosenTimeslot &&
            priceCount > 0 &&
            priceCount < addon.minQuantity
          ) {
            return true;
          }
          // if time slot quantity exceeds available
          const timeSlotAvailablity =
            addonsData.value[addonId].chosenTimeslot?.availability || 0;

          if (priceCount > timeSlotAvailablity) {
            return true;
          }
          // if recurring addon quantity exceeds max available
          if (priceCount > addon.maxQuantity) {
            return true;
          }
        }
        // if not recurring AND has no timeslots AND is not retail
        else if (
          !addonPC.isRecurring &&
          !(addonsData.value[addonId].timeslots || []).length &&
          !isRetail
        ) {
          if (addon.isMandatory) {
            return true;
          } else if (priceCount > 0) {
            return true;
          }
        }
        // Products booked through a time slot (gift cards, and non-recurring
        // ticket/event add-ons with an auto-selected slot) carry their
        // availability on the chosen time slot, not on price.quantity (which is
        // 0 for them). Cap on the slot's availability instead.
        else if (
          addonsData.value[addonId].chosenTimeslot &&
          priceCount >
            (addonsData.value[addonId].chosenTimeslot?.availability ?? 0)
        ) {
          return true;
        }
        // Stock-based products without a time slot (retail) cap on price.quantity.
        else if (
          !addonsData.value[addonId].chosenTimeslot &&
          !sellOnSoldOut(addonPC.prices, addonPC.type) &&
          !sharedStocksWithOverbooking(addonPC) &&
          !isGiftCard &&
          priceCount > price.quantity
        ) {
          return true;
        }
        // if retail is not mandatory, user selected increased quantity but it doesn't meet minimum quantity requirement.
        else if (
          !addon.isMandatory &&
          priceCount > 0 &&
          priceCount < addon.minQuantity
        ) {
          return true;
        }
        // if addon quantity exceeds max available
        else if (priceCount > addon.maxQuantity) {
          return true;
        } else if (priceCount < priceCountConfirmedInBooking) {
          return true;
        }
      }
      if (packageMaxQtyError.value || packageMinQtyError.value) {
        return true;
      }
      return false;
    });

    const pricesDiff = computed(() => {
      let diffs: Record<string, number> = {};
      if (!pricesCount.value) {
        return {};
      }

      for (const priceUuid of Object.keys(pricesCount.value)) {
        diffs[priceUuid] =
          (pricesCount.value[priceUuid] ?? 0) -
          (pricesBookingCount.value[priceUuid] ?? 0);
      }
      return diffs;
    });

    const modalTitle = computed(() => {
      if (timeSlotsModalVisible.value) {
        return t("addons.timeslots");
      }
      return t("addons.addons");
    });

    const currency = computed(
      () => selectedOrder.value?.orderSession.currency || Currency.CHF
    );

    const modalHeaderTitle = computed(() => {
      const selectedAddon = timeSlotsModalData.value?.addon;
      if (timeSlotsModalVisible.value && selectedAddon) {
        const addonPC = addonsData.value[selectedAddon.id].pc;
        const addonPrice = addonsData.value[selectedAddon.id].price;
        const addonProductName =
          addonPC.product.nameLocale[currentLanguage.value] ||
          addonPC.product.name ||
          "";
        const addonPriceName = priceName(addonPC, addonPrice);
        let addonTitle = `${addonProductName} - ${addonPriceName}`;
        return addonTitle;
      }
      return productCategoryName.value;
    });

    const showAddonsDates = computed(() => {
      for (const addonId in addonsData.value) {
        const addon = addonsData.value[addonId];
        if (addon.pc.isRecurring) {
          return true;
        }
      }
      return false;
    });

    /**
     * Calculates minimum and maximum quantities allowed for addons
     * according to backend logic, the total number of first step addons should be the number of booked main ticket multiplied by price.packageMin(Max)Quantity
     */
    const packageQuantities = computed(() => {
      let minQuantity = 0;
      let maxQuantity = 0;
      const pricesToAddToBasket = props.modalProps.props?.pricesToAddToBasket;
      if (!pricesToAddToBasket) {
        return { minQuantity, maxQuantity };
      }
      for (const priceInfo of pricesToAddToBasket) {
        const { price, pssQuantity } = priceInfo;
        const alreadyBookedQuantity = orderSummary.value?.orderLines
          .filter((line) => line.priceId === price.uuid)
          .reduce((acc, line) => acc + line.quantity, 0);

        const packageMinQuantity = price.packageMinQuantity || 0; // use 0 as a fallback value to allow at least 1 addon
        const packageMaxQuantity = price.packageMaxQuantity || 999; // use a large number as a fallback value to not block sales
        minQuantity +=
          (pssQuantity + alreadyBookedQuantity) * packageMinQuantity;
        maxQuantity +=
          (pssQuantity + alreadyBookedQuantity) * packageMaxQuantity;
      }
      return { minQuantity, maxQuantity };
    });

    const packageMaxQtyError = computed(() => {
      const packageMaxQuantity = packageQuantities.value.maxQuantity;
      let totalAddonTickets = 0;
      for (const addon of addons.value) {
        totalAddonTickets += pricesCount.value[addon.priceUuid] || 0;
      }
      return totalAddonTickets > packageMaxQuantity;
    });

    const packageMinQtyError = computed(() => {
      let numberTickets = 0;
      const packageMinQuantity = packageQuantities.value.minQuantity;
      for (const addon of addons.value) {
        numberTickets += pricesCount.value[addon.priceUuid] || 0;
      }
      if (numberTickets && numberTickets < packageMinQuantity) {
        return true;
      }
      return false;
    });

    /**
     * Reduces the time range for fetching addon timeslots to [max(startOfSelectedDay, from), min(startOfSelectedDay, to)]
     * This avoids fetching and storing all timeslots if an addon can be booked days before or after the main ticket
     * @param from addon original bookable time range start
     * @param to addon original bookable time range end
     * @param addonBeforeOffset number of days an addon can be booked before the main ticket start time
     * @param addonAfterOffset number of days an addon can be booked after the main ticket start time
     * @returns (from, to) - adjusted time range to the current selected day
     */
    function adjustAddonInterval(
      from: string,
      to: string,
      addonBeforeOffset: number,
      addonAfterOffset: number
    ) {
      // When the addon is bookable on days other than the main ticket's (either offset
      // spans at least one day), clip the fetch window to the currently selected day,
      // intersected with the full bookable range. Both bounds must be clipped (not just
      // the one matching the offset direction): otherwise a day after the main ticket
      // keeps `from` pinned to the main ticket's start time and hides that day's morning
      // slots, and a day before keeps `to` pinned and hides that day's evening slots.
      if (addonBeforeOffset >= 1 || addonAfterOffset >= 1) {
        from = dayjs
          .max(dayjs(selectedDate.value as Date).startOf("day"), dayjs(from))
          .toISOString();
        to = dayjs
          .min(dayjs(selectedDate.value as Date).endOf("day"), dayjs(to))
          .toISOString();
      }
      return { from, to };
    }

    /**
     * Fetches timeslots for recurring addons
     * @param addon addon to fetch timeslots for
     * @param addonPC product category of the addon
     * @param options.fromRemoteFirst if set to true, it will fetch timeslots from backend before checking ones that are stored locally
     * @returns timeslotsData
     * @returns timeslotsData.timeslots - timeslots fof the addon
     * @returns timeslotsData.addonBeforeOffset - number of days the addon can start before the main ticket
     * @returns timeslotsData.addonAfterOffset - number of days the addon can start after the main ticket
     */
    async function getAddonTimeSlotsData(
      addon: IPackagePrice,
      addonPC: IProductCategory,
      options?: { fromRemoteFirst: boolean }
    ): Promise<{
      timeslots: ITimeSlot[];
      addonBeforeOffset: number;
      addonAfterOffset: number;
    }> {
      // Giftcard timeslot is independent of the parent ticket's time;
      // fetch it directly instead of querying within an offset window.
      if (addonPC.type === PCTypes[PCTypes.GiftCard]) {
        const giftCardTimeslot = await getGiftCardAddonTimeslot(addonPC);
        return {
          timeslots: giftCardTimeslot ? [giftCardTimeslot] : [],
          addonBeforeOffset: 0,
          addonAfterOffset: 0,
        };
      }

      let timeslots: ITimeSlot[] = [];

      // number of days the addon can start before the main ticket
      let addonBeforeOffset = 0;

      // number of days the addon can start after the main ticket
      let addonAfterOffset = 0;

      const startDateTime = mainTicketTimeSlot.value?.startDateTime;
      const mainTicketStartDateTime = startDateTime
        ? dayjs(startDateTime).utc()
        : dayjs().utc();

      if (!addonPC.isRecurring) {
        // Non-recurring addons
        // pick all available timeslots still valid at main ticket start.
        const addonTimeslots = await queryTimeslotsByProductCategories({
          categoryIds: [Number(addonPC.id)],
          endDateTimeAfter: mainTicketStartDateTime.toISOString(),
        });
        timeslots = addonTimeslots.filter((ts) => ts.availability >= 1);
        return { timeslots, addonBeforeOffset, addonAfterOffset };
      }

      const {
        minutesBeforeStartMin,
        minutesBeforeStartMax,
        minutesAfterStartMin,
        minutesAfterStartMax,
      } = addon;
      if (minutesBeforeStartMin || minutesAfterStartMin) {
        // if these values were set (currently only with an L2), then we use them to determine addons time intervals instead of minAfter and minBefore
        let beforeStartFrom = mainTicketStartDateTime
          .subtract(minutesBeforeStartMin, "minute")
          .toISOString(); // may change
        const beforeStartTo = mainTicketStartDateTime
          .subtract(minutesBeforeStartMax, "minute")
          .toISOString();
        const afterStartFrom = mainTicketStartDateTime
          .add(minutesAfterStartMin, "minute")
          .toISOString();
        let afterStartTo = mainTicketStartDateTime
          .add(minutesAfterStartMax, "minute")
          .toISOString(); // may change

        addonBeforeOffset = dayjs(mainTicketStartDateTime).diff(
          beforeStartFrom,
          "day"
        );
        addonAfterOffset = dayjs(afterStartTo).diff(
          mainTicketStartDateTime,
          "day"
        );

        // adjust the time range if necessary and reassign beforeStartFrom, afterStartTo
        ({ from: beforeStartFrom, to: afterStartTo } = adjustAddonInterval(
          beforeStartFrom,
          afterStartTo,
          addonBeforeOffset,
          addonAfterOffset
        ));

        if (options?.fromRemoteFirst) {
          // we try fetching from backend first
          addonsLoading.value = true;
          try {
            if (minutesBeforeStartMin && minutesBeforeStartMax) {
              // extended before start
              await fetchAllPCsTimeslotsByTimeRange(
                [addonPC.id],
                dayjs(beforeStartFrom).toDate(),
                dayjs(beforeStartTo).toDate(),
                true
              );
            }
            if (minutesAfterStartMin && minutesAfterStartMax) {
              // extended after start
              await fetchAllPCsTimeslotsByTimeRange(
                [addonPC.id],
                dayjs(afterStartFrom).toDate(),
                dayjs(afterStartTo).toDate(),
                true
              );
            }
          } catch (error) {
            LogService.info(
              "Error while loading addons timeslots from backend",
              {
                addonPCID: addonPC.id,
                date: mainTicketStartDateTime.toISOString(),
                error,
              }
            );
            NotificationService.showGeneralResponseError();
          } finally {
            addonsLoading.value = false;
          }
        }
        if (minutesBeforeStartMin && minutesBeforeStartMax) {
          // extended before start
          const firstIntervalTimeSlots =
            (await getFutureTimeSlotsByProductCategory2(
              Number(addonPC.id),
              beforeStartFrom,
              beforeStartTo
            )) || [];
          // add timeslots
          timeslots = timeslots.concat(firstIntervalTimeSlots);
        }
        if (minutesAfterStartMin && minutesAfterStartMax) {
          // extended after start
          const secondIntervalTimeSlots =
            (await getFutureTimeSlotsByProductCategory2(
              Number(addonPC.id),
              afterStartFrom,
              afterStartTo
            )) || [];
          // add timeslots
          timeslots = timeslots.concat(secondIntervalTimeSlots);
        }
      } else {
        // we use the usual minBefore, minAfter logic
        const minAfter = addon.minutesAfter || 0;
        const minBefore = addon.minutesBefore || 0;
        let from = mainTicketStartDateTime
          .subtract(minBefore, "minute")
          .toISOString();
        let to = mainTicketStartDateTime.add(minAfter, "minute").toISOString();

        addonBeforeOffset = dayjs(mainTicketStartDateTime).diff(from, "day");
        addonAfterOffset = dayjs(to).diff(mainTicketStartDateTime, "day");

        // adjust the time range if necessary and reassign from and to
        ({ from, to } = adjustAddonInterval(
          from,
          to,
          addonBeforeOffset,
          addonAfterOffset
        ));

        if (options?.fromRemoteFirst) {
          // we try fetching from backend first
          addonsLoading.value = true;
          try {
            await fetchAllPCsTimeslotsByTimeRange(
              [addonPC.id],
              dayjs(from).toDate(),
              dayjs(to).toDate(),
              true
            );
          } catch (error) {
            LogService.info(
              "Error while loading addons timeslots from backend",
              {
                addonPCID: addonPC.id,
                date: mainTicketStartDateTime.toISOString(),
                error,
              }
            );
            NotificationService.showGeneralResponseError();
          } finally {
            addonsLoading.value = false;
          }
        }

        // check if timeslots are in local db, otherwise fetch from backend
        timeslots =
          (await getFutureTimeSlotsByProductCategory2(
            Number(addonPC.id),
            from,
            to
          )) || [];
      }

      if (!timeslots.length && !options?.fromRemoteFirst) {
        // retry with checking timeslots from backend first
        return getAddonTimeSlotsData(addon, addonPC, {
          fromRemoteFirst: true,
        });
      }

      return {
        timeslots,
        addonBeforeOffset,
        addonAfterOffset,
      };
    }

    /**
     * Retrieves and initializes data for each addon related to the product category passed in the modal props.
     */
    async function getAddons() {
      // get main product category
      if (!props.modalProps.props?.productCategory) {
        return;
      }
      mainProductCategory.value = props.modalProps.props?.productCategory;
      if (!isMainPcRetail.value) {
        mainTicketTimeSlot.value = props.modalProps.props?.timeslot || null;
      }
      selectDate(dayjs(mainTicketTimeSlot.value?.startDateTime).toDate());
      // get related addons
      const pricesToAddToBasket = props.modalProps.props?.pricesToAddToBasket;
      if (!pricesToAddToBasket) {
        return;
      }
      for (const price of pricesToAddToBasket) {
        const priceAddons = await getPriceNonAutomaticAddons(price.price);
        for (const addon of priceAddons) {
          const alreadyExistingAddon = addons.value.find(
            (a) => a.priceUuid === addon.priceUuid
          );
          if (alreadyExistingAddon) {
            /**
             * In case multiple prices were selected, some of which have addons that overlap:
             * 1. mandatory if one of addons is mandatory
             * 2. min/max quantity is the larger/smaller value, respectively, between the two
             */
            alreadyExistingAddon.isMandatory =
              addon.isMandatory || alreadyExistingAddon.isMandatory;
            alreadyExistingAddon.minQuantity = Math.max(
              alreadyExistingAddon.minQuantity,
              addon.minQuantity
            );
            alreadyExistingAddon.maxQuantity = Math.min(
              alreadyExistingAddon.maxQuantity,
              addon.maxQuantity
            );
          } else {
            addons.value.push(addon);
          }
        }
      }

      // get a record containing prices, product category and timeslots for each addon
      for (const addon of addons.value) {
        const pcByPrice = await getProductCategoryByPriceId(addon.priceId);
        if (pcByPrice) {
          const addonPC = pcByPrice;
          let timeSlotsData:
            | {
                timeslots: ITimeSlot[];
                addonBeforeOffset: number;
                addonAfterOffset: number;
              }
            | undefined;
          let chosenTimeslot: ITimeSlot | undefined = undefined;
          // check if addon already has a chosen timeslot (in case of returning to the addons step from the timeslot selection step),
          // if not initialize timeslot data for recurring addons or for non recurring addons with only 1 timeslot
          const lineInOrder = initialOrderLines.find(
            (line) =>
              line.parentLineId &&
              line.priceId === addon.priceUuid &&
              line.state !== OrderSessionState.ABANDONED &&
              line.lineType === LineType.SaleLine
          );

          if (lineInOrder?.timeSlotId) {
            const timeslot = await getTimeslotById(lineInOrder.timeSlotId);

            if (timeslot) {
              chosenTimeslot = timeslot;
            }
          } else {
            const isRecurring = addonPC.isRecurring;
            const hasSingleTimeslot = addonPC.timeSlotCount === 1;
            const shouldLoadTimeslots = isRecurring || hasSingleTimeslot;

            if (shouldLoadTimeslots) {
              // get timeslots for ticket addons
              timeSlotsData = await getAddonTimeSlotsData(addon, addonPC);

              // If addon is not recurring but has exactly one timeslot (e.g. single date event),
              // automatically select the only available timeslot
              if (!isRecurring && hasSingleTimeslot) {
                chosenTimeslot = timeSlotsData.timeslots[0];
              }
            }
          }

          const price = addonPC.prices.find(
            (price) => price.uuid === addon.priceUuid
          );
          if (!price) {
            continue;
          }
          addonsData.value[addon.id] = {
            pc: addonPC,
            price,
            timeslots: timeSlotsData?.timeslots,
            addonBeforeOffset: timeSlotsData?.addonBeforeOffset,
            addonAfterOffset: timeSlotsData?.addonAfterOffset,
            chosenTimeslot,
          };
          // bookable days are days which fit in the range of [mainTicketStart - addonBeforeOffset, mainTicketStart + addonAfterOffset]
          // now in the calendar, we only display bookable days to avoid giving the impression that an addon is available on a day that it isn't
          // this is done by keeping track of which addon has the largest offset from the main ticket start time
          if (
            !timeSlotsData?.addonBeforeOffset &&
            !timeSlotsData?.addonAfterOffset
          ) {
            // skip addons that are not recurring or are only bookable the same day as
            // the main ticket (both offsets are 0). An addon bookable several days only
            // before OR only after the main ticket still has one non-zero offset and
            // must contribute its day range to the calendar.
            continue;
          }

          const mainTicketStartDateTime = dayjs(
            mainTicketTimeSlot.value?.startDateTime
          );
          if (mainTicketStartDateTime.isSame(dayjs(), "day")) {
            // prevent displaying days of the past
            maxDaysBeforeMainTicket.value = 0;
          } else if (
            mainTicketStartDateTime
              .subtract(timeSlotsData.addonBeforeOffset, "day")
              .isBefore(dayjs(), "day")
          ) {
            // prevent displaying dates of the past if applying the offset takes us back to before today
            maxDaysBeforeMainTicket.value = mainTicketStartDateTime.diff(
              dayjs(),
              "day"
            );
          } else {
            maxDaysBeforeMainTicket.value = Math.max(
              maxDaysBeforeMainTicket.value,
              timeSlotsData.addonBeforeOffset
            );
          }
          maxDaysAfterMainTicket.value = Math.max(
            maxDaysAfterMainTicket.value,
            timeSlotsData.addonAfterOffset
          );
        }
      }

      if (!addonsData.value) {
        return;
      }

      // For the current product, find each main-ticket parent sale line and
      // sum the quantities of its addon sale lines that match this addon's
      // price + displayed value.
      const mainParentLines = initialOrderLines.filter(
        (line) =>
          line.productId === mainProductCategory.value?.product.uuid &&
          !line.parentLineId &&
          line.lineType === LineType.SaleLine &&
          line.state !== OrderSessionState.ABANDONED
      );

      Object.keys(addonsData.value).forEach((id) => {
        const addonId = Number(id);
        const addon = addons.value.find((addon) => addon.id === addonId);
        // addon.displayedValue is in cents; line.unitPrice / overridePrice are in major units
        const addonDisplayedValue = (addon?.displayedValue || 0) / 100;
        const priceUuid = addonsData.value[addonId].price.uuid;

        if (!mainParentLines.length) {
          pricesCount.value[priceUuid] = 0;
          pricesBookingCount.value[priceUuid] = 0;
          return;
        }

        let countInBooking = 0;
        mainParentLines.forEach((parentLine) => {
          countInBooking += initialOrderLines
            .filter(
              (line) =>
                line.parentLineId === parentLine.id &&
                line.priceId === priceUuid &&
                (line.unitPrice === addonDisplayedValue ||
                  line.overridePrice === addonDisplayedValue) &&
                line.state !== OrderSessionState.ABANDONED &&
                line.lineType === LineType.SaleLine
            )
            .reduce((acc, line) => acc + line.quantity, 0);
        });
        pricesCount.value[priceUuid] = countInBooking;
        pricesBookingCount.value[priceUuid] = countInBooking;
      });
    }

    /**
     * If merch product then return retailOption name
     * If ticket product then return priceName
     */
    function priceName(pc: IProductCategory, price: IProductSlotPrice) {
      // if it is a ticket product then we return the translated name of the price if there is translation, otherwise we return the price name
      // if it is a retail product then we return the translated name of the retail if there is translation, otherwise we return the price name
      // if it is a retail product but there are not retail options, there is only the default retail, then we consider the categoryName name and its translations
      const isRetail = pc.type === PCTypes[PCTypes.Retail];
      if (!isRetail) {
        return price.nameLocale
          ? price.nameLocale[currentLanguage.value] || price.name
          : price.name;
      }

      if (isRetail) {
        if (price.retail[0].type !== RetailOptionType.Default_Retail_Option) {
          return price.retail[0].nameLocale
            ? price.retail[0].nameLocale[currentLanguage.value] ||
                price.retail[0].name
            : price.retail[0].name;
        }
        if (price.retail[0].type === RetailOptionType.Default_Retail_Option) {
          return pc?.nameLocale[currentLanguage.value] || pc?.name;
        }
      }
    }

    function getDefaultRetailOptionSmeetzPicture(
      pc: IProductCategory
    ): ISmeetzPicture | undefined {
      const smeetzPicture = pc.prices[0].retail[0].smeetzPictures[0];
      return smeetzPicture;
    }

    function productPicture(pc: IProductCategory) {
      if (pc) {
        const isRetail = pc.type === PCTypes[PCTypes.Retail];
        if (!isRetail) {
          return pc?.product.pictureUrl;
        } else {
          return (
            createImageproxyUrl(getDefaultRetailOptionSmeetzPicture(pc), {
              width: 150 /*Number(PCSELECT_IMAGE_WIDTH)*/,
              height: 150 /*Number(PCSELECT_IMAGE_HEIGHT)*/,
            }) || shoppingIcon
          );
        }
      } else {
        return shoppingIcon;
      }
    }

    function sharedStocksWithOverbooking(pc: IProductCategory) {
      return pc.stockIds && pc.stockIds.length && pc.stockIds.includes(-1000);
    }

    async function addToBasketWithAddons() {
      isLoading.value = true;
      let allAddons: ICreateAddons[] = [];

      if (addonsData.value)
        for (const addon of addons.value) {
          const addonPC = addonsData.value[addon.id].pc;
          const addonPrice = addonsData.value[addon.id].price;
          // const isRetail = addonPC.type === PCTypes[PCTypes.Retail];
          const chosenTimeslot = addonsData.value[addon.id].chosenTimeslot;
          const priceDiff = pricesDiff.value[addonPrice.uuid];

          if (priceDiff === 0) {
            continue;
          }

          // if price quantity increased
          allAddons.push({
            productCategory: addonPC,
            priceId: addon.priceId,
            count: priceDiff,
            timeslot: chosenTimeslot,
            automaticallyBook: addon.automaticallyBook,
            packagePriceId: addon.id,
            sendTicket: addon.sendTicket,
            // priceValue: addon.displayedValue || 0,
            // pssType: isRetail
            //   ? PSSType[PSSType.Retail]
            //   : PSSType[PSSType.Ticket],
          });
        }

      const addonModalProps = props.modalProps.props;
      if (!addonModalProps) {
        isLoading.value = false;
        hideDialog();
        return;
      }
      const { isRetail, productCategory, pricesToAddToBasket, timeslot } =
        addonModalProps;
      if (!pricesToAddToBasket) {
        isLoading.value = false;
        return;
      }

      for (const priceToAdd of pricesToAddToBasket) {
        const { price, pssQuantity, overridePrice } = priceToAdd;
        const addonsToAdd: ICreateAddons[] = [];
        /**
         * Find if the current price has addons
         * If it does, add its addons to addonsToAdd for insert and remove it from allAddons so it isn't inserted again
         */
        allAddons = allAddons.filter((addon) => {
          const toAdd = price.addons.some(
            (priceAddon) => priceAddon.id === addon.packagePriceId
          );
          if (toAdd) {
            addonsToAdd.push(addon);
            return false;
          }
          return true;
        });

        try {
          await addToBasket({
            pssQuantity: pssQuantity,
            isRetail: isRetail,
            nonAutomaticAddons: addonsToAdd,
            price: price,
            overridePrice,
            productCategory,
            timeslot,
          });
        } catch (e) {
          LogService.info(
            "Error while adding product with addons to basket",
            {
              quantity: price.quantity,
              addons: addonsToAdd,
              error: e,
            },
            LogLevel.Error
          );
        }
      }
      isLoading.value = false;
      hideDialog();
    }

    function priceMaxQuantityLimit(
      addon: IPackagePrice,
      pc: IProductCategory,
      priceUuid: string,
      constraint: PriceMaxQtyConstraint
    ) {
      const isRetail = pc.type === PCTypes[PCTypes.Retail];
      // if the price is not allowed to be oversold, and its availability is less than the addon's max quantity , we reduce the latter to not oversell
      if (
        !sellOnSoldOut(pc.prices, pc.type) &&
        !sharedStocksWithOverbooking(pc)
      ) {
        if (isRetail) {
          const quantity =
            pc.prices.find((p) => p.uuid === priceUuid)?.quantity || 0;
          addon.maxQuantity = Math.min(addon.maxQuantity, quantity);
        } else {
          const chosenTimeslot = addonsData.value[addon.id].chosenTimeslot;
          if (!chosenTimeslot) {
            // return false because not choosing a timeslot already prevents confirming
            return { maxQtyLimit: false };
          }
          const timeSlotAvailablity =
            addonsData.value[addon.id].chosenTimeslot?.availability || 0;
          addon.maxQuantity = Math.min(addon.maxQuantity, timeSlotAvailablity);
        }
      }

      const priceCount = pricesCount.value[priceUuid];
      // initially, an addon's max quantity is it's maxQuantity field
      let maxQuantity = addon.maxQuantity;
      let maxQuantityReason = PriceQtyLimitReason.ADDON;
      // apply package-level constraint: total addons across all types limited by packageMaxQuantity
      const packageMaxQuantity = packageQuantities.value.maxQuantity;
      let otherAddonsCount = 0;
      for (const otherAddon of addons.value) {
        if (otherAddon.id !== addon.id) {
          otherAddonsCount += pricesCount.value[otherAddon.priceUuid] || 0;
        }
      }
      const remainingPackageCapacity = packageMaxQuantity - otherAddonsCount;
      if (remainingPackageCapacity < maxQuantity) {
        maxQuantity = remainingPackageCapacity;
        maxQuantityReason = PriceQtyLimitReason.PACKAGE;
      }

      // sellOnSoldOut bypasses stock-based (ADDON) limits but NOT package-level limits
      const canBypassLimit =
        maxQuantityReason === PriceQtyLimitReason.ADDON &&
        (sellOnSoldOut(pc.prices, pc.type) ||
          !!sharedStocksWithOverbooking(pc));

      if (
        constraint === PriceMaxQtyConstraint.REACHED &&
        priceCount >= maxQuantity &&
        !canBypassLimit
      ) {
        return { maxQtyLimit: true, reason: maxQuantityReason };
      } else if (
        constraint === PriceMaxQtyConstraint.EXCEEDED &&
        priceCount > maxQuantity &&
        !canBypassLimit
      ) {
        return { maxQtyLimit: true, reason: maxQuantityReason };
      }
      return { maxQtyLimit: false };
    }

    function priceMinQuantityLimit(
      addon: IPackagePrice,
      pc: IProductCategory,
      priceUuid: string
    ) {
      const priceCount = pricesCount.value[priceUuid];
      if (!addon.isMandatory && priceCount === 0) {
        return false;
      }
      const isRetail = pc.type === PCTypes[PCTypes.Retail];
      if (isRetail && priceCount < addon.minQuantity) {
        return true;
      } else if (
        !isRetail &&
        addonsData.value[addon.id].chosenTimeslot &&
        priceCount < addon.minQuantity
      ) {
        return true;
      }
      return false;
    }

    function canDecrease(
      addon: IPackagePrice,
      pc: IProductCategory,
      priceUuid: string
    ) {
      // do not allow decreasing if all currently displayed addons are already confirmed
      // (i.e. on a previously completed session) — those can only be cancelled, not decremented.
      const confirmedAddonQuantity = (orderSummary.value?.orderLines || [])
        .filter(
          (line) =>
            line.parentLineId &&
            line.priceId === priceUuid &&
            line.state === OrderSessionState.COMPLETED &&
            line.lineType === LineType.SaleLine
        )
        .reduce((sum, line) => sum + line.quantity, 0);

      if (confirmedAddonQuantity === pricesCount.value[priceUuid]) {
        return { res: false };
      }
      if (
        priceMinQuantityLimit(addon, pc, priceUuid) ||
        packageMinQtyError.value ||
        pricesCount.value[addonsData.value[addon.id].price.uuid] <= 0
      ) {
        return { res: false };
      }
      return { res: true };
    }

    function canIncrease(
      addon: IPackagePrice,
      pc: IProductCategory,
      priceUuid: string
    ) {
      if (
        addonsData.value[addon.id].pc.isRecurring &&
        !addonsData.value[addon.id].chosenTimeslot
      ) {
        return { res: false, shakeText: true };
      }
      if (
        priceMaxQuantityLimit(
          addon,
          pc,
          priceUuid,
          PriceMaxQtyConstraint.REACHED
        ).maxQtyLimit
      ) {
        return { res: false };
      }
      return { res: true };
    }

    function increase(
      addon: IPackagePrice,
      pc: IProductCategory,
      priceUuid: string
    ) {
      const { res, shakeText: shouldShake } = canIncrease(addon, pc, priceUuid);
      if (!res) {
        if (shouldShake) {
          shakeText(addon.id);
        }
        return;
      }
      const priceCount = pricesCount.value[priceUuid];
      pricesCount.value[priceUuid] = priceCount + 1;
    }
    function decrease(
      addon: IPackagePrice,
      pc: IProductCategory,
      priceUuid: string
    ) {
      const priceCount = pricesCount.value[priceUuid];

      if (priceCount === 0) {
        return;
      }

      if (!canDecrease(addon, pc, priceUuid).res) {
        return;
      }
      pricesCount.value[priceUuid] = priceCount - 1;
    }

    function hideDialog() {
      if (
        props.modalProps.referrer === ModalComponents.Products &&
        props.modalProps.goBack
      ) {
        ModalModule.setModalProps({});
        ModalModule.setStatus(ModalStatus.Products);
      } else {
        ModalModule.setStatus(ModalStatus.Hidden);
      }
    }

    function groupTimeSlots(timeslots: ITimeSlot[]) {
      // sort by startDateTime ascending
      const sorted = [...timeslots].sort(
        (a, b) =>
          dayjs(a.startDateTime).valueOf() - dayjs(b.startDateTime).valueOf()
      );

      const groupedTimeSlots: Record<string, Record<string, ITimeSlot[]>> = {};

      sorted.forEach((timeslot) => {
        const day = dayjs(timeslot.startDateTime).format("dddd DD MMM YYYY");
        const hour = dayjs(timeslot.startDateTime).format("HH:mm");

        if (!groupedTimeSlots[day]) {
          groupedTimeSlots[day] = {};
        }
        if (!groupedTimeSlots[day][hour]) {
          groupedTimeSlots[day][hour] = [];
        }

        groupedTimeSlots[day][hour].push(timeslot);
      });

      return groupedTimeSlots;
    }

    function chooseTimeSlot(data: { addonId: number; timeslot: ITimeSlot }) {
      addonsData.value[data.addonId].chosenTimeslot = data.timeslot;
      timeSlotsModalData.value = null;
      timeSlotsModalVisible.value = false;
    }

    function setShowTimeSlotsModal(
      show: boolean,
      addon?: IPackagePrice,
      timeslots?: ITimeSlot[]
    ) {
      if (show && addon && timeslots) {
        const priceCount =
          pricesCount.value[addonsData.value[addon.id].price.uuid];
        let filteredTimeSlots = timeslots;
        if (priceCount) {
          filteredTimeSlots = timeslots.filter((timeslot) => {
            return timeslot.availability >= priceCount;
          });
        }
        const groupedTimeSlots = groupTimeSlots(filteredTimeSlots);
        timeSlotsModalData.value = {
          addon,
          groupedTimeSlots,
        };
      } else {
        timeSlotsModalData.value = null;
      }
      timeSlotsModalVisible.value = show;
    }

    function isDateDisabled(date: Date) {
      const dateJS = dayjs(date);
      // Disable before today
      if (dateJS.isBefore(dayjs(), "day")) {
        return true;
      }

      // Disable dates outside the addon's bookable range around the main ticket
      // (e.g. an addon bookable up to N days after the main ticket can't be booked later)
      const mainTicketStartDateTime = dayjs(
        mainTicketTimeSlot.value?.startDateTime
      );
      const earliestBookableDay = mainTicketStartDateTime.subtract(
        maxDaysBeforeMainTicket.value,
        "day"
      );
      const latestBookableDay = mainTicketStartDateTime.add(
        maxDaysAfterMainTicket.value,
        "day"
      );
      if (
        dateJS.isBefore(earliestBookableDay, "day") ||
        dateJS.isAfter(latestBookableDay, "day")
      ) {
        return true;
      }

      return false;
    }

    // whether the currently selected date is not one of the inline day buttons
    // (i.e. it was picked from the calendar) — used to show the calendar button as selected
    function getIsSelectedDateHidden() {
      return !getAddonCalendarDays().some((day) =>
        dayjs(selectedDate.value as Date).isSame(day, "day")
      );
    }

    async function selectDate(date?: Date) {
      if (date) {
        selectedDate.value = date;
      }
      // else if no date argument passed then it was selected from calendar
      const selectedAddon = timeSlotsModalData.value?.addon;
      if (selectedAddon && timeSlotsModalVisible.value) {
        // if we have a selected addon and we're in the timeslots step, we refetch
        // its timeslots for the newly selected day. The fetch window is clipped to
        // a single day in adjustAddonInterval, so the cached timeslots only cover the
        // initial (main ticket) day; without refetching, days other than the main
        // ticket's would show no slots for addons bookable multiple days before/after.
        const addonData = addonsData.value[selectedAddon.id];
        // changing the day invalidates any slot chosen for the previous day
        addonData.chosenTimeslot = undefined;
        const { timeslots } = await getAddonTimeSlotsData(
          selectedAddon,
          addonData.pc
        );
        addonData.timeslots = timeslots;
        setShowTimeSlotsModal(true, selectedAddon, timeslots);
      } else {
        // reload timeslots for addons that need it
        for (const addon of addons.value) {
          const addonData = addonsData.value[addon.id];
          if (addonData.addonAfterOffset || addonData.addonBeforeOffset) {
            // changing the day invalidates any slot chosen for the previous day
            addonData.chosenTimeslot = undefined;
            // refetch timeslots
            const timeSlotsData = await getAddonTimeSlotsData(
              addon,
              addonData.pc
            );
            addonData.timeslots = timeSlotsData.timeslots;
          }
        }
      }

      // bring this back if we need to clear chosen timeslots when not in the addons step
      // if (!timeSlotsModalVisible.value) {
      //   // if we're not in the timeslots step, we clear the chosen timeslot
      //   for (const id in addonsData.value) {
      //     delete addonsData.value[id].chosenTimeslot;
      //   }
      //   timeSlotsModalData.value = null;
      // }
    }

    // gets which days to display in the calendar boxes
    function getAddonCalendarDays() {
      const mainTicketStartDateTime = dayjs(
        mainTicketTimeSlot.value?.startDateTime
      );
      const calendarDays = [mainTicketStartDateTime]; // Max length should be 5 for UI

      // Handle previous days
      const maxPrevDays = maxDaysBeforeMainTicket.value || 0;
      addDaysToCalendar(
        calendarDays,
        mainTicketStartDateTime,
        maxPrevDays,
        "subtract"
      );

      // Handle next days
      const maxNextDays = maxDaysAfterMainTicket.value || 0;
      addDaysToCalendar(
        calendarDays,
        mainTicketStartDateTime,
        maxNextDays,
        "add"
      );

      return calendarDays;
    }

    // Helper function to add days to the calendar
    function addDaysToCalendar(
      calendarDays: Dayjs[],
      mainDate: Dayjs,
      maxDays: number,
      operation: "subtract" | "add"
    ) {
      for (let i = 0; i < maxDays; i++) {
        const newDate = mainDate[operation](i + 1, "day");
        if (operation === "subtract") {
          calendarDays.unshift(newDate);
        } else {
          calendarDays.push(newDate);
        }

        // Only show two days before/after the main ticket date, the rest are selectable from the date picker
        if (i === 1 && i < maxDays - 1) {
          if (!showDatePicker.value) {
            showDatePicker.value = true;
          }
          break;
        }
      }
    }

    const headerMargin = computed(() => {
      // adds some space for the back button on timeslots step
      if (timeSlotsModalVisible.value) {
        return "4rem";
      }
      return "0";
    });

    onMounted(() => {
      getAddons();
    });

    // Function-ref Map for shake-animation targets. Each addon row may have a
    // `selectTimeSlot-{id}` button OR a `noTimeSlot-{id}` paragraph (mutually
    // exclusive via v-if/v-else-if/v-else), which `shakeText` looks up to
    // briefly play a shake animation when the user tries to confirm without
    // picking a timeslot.
    const shakeRefs = new Map<string, HTMLElement>();
    const setShakeRef = (key: string) => (el: unknown) => {
      if (el instanceof HTMLElement) {
        shakeRefs.set(key, el);
      } else {
        shakeRefs.delete(key);
      }
    };
    const shakeText = debounce((addonId: number) => {
      const el =
        shakeRefs.get(`selectTimeSlot-${addonId}`) ??
        shakeRefs.get(`noTimeSlot-${addonId}`);
      if (!el) return;
      el.classList.add("shake-animation");
      setTimeout(() => el.classList.remove("shake-animation"), 500);
    }, 500);

    return {
      t,
      dialogVisible,
      mainProductCategory,
      formatDateTime,
      addonsData,
      priceName,
      productPicture,
      pcselectPictureSize,
      isLoading,
      isDisabled,
      increase,
      decrease,
      hideDialog,
      pricesCount,
      addToBasketWithAddons,
      PCTypes,
      chooseTimeSlot,
      timeSlotsModalVisible,
      timeSlotsModalData,
      setShowTimeSlotsModal,
      dayjs,
      sortedAddons,
      isDateDisabled,
      parseDate,
      getLocalTimeZone,
      selectedDate,
      selectedDateValue,
      selectDate,
      currency,
      modalTitle,
      modalHeaderTitle,
      headerMargin,
      showAddonsDates,
      priceMaxQuantityLimit,
      PriceMaxQtyConstraint,
      priceMinQuantityLimit,
      PriceQtyLimitReason,
      canIncrease,
      addonsLoading,
      getAddonCalendarDays,
      getIsSelectedDateHidden,
      showDatePicker,
      areObjectsEqual,
      packageMaxQtyError,
      packageMinQtyError,
      packageQuantities,
      canDecrease,
      setShakeRef,
    };
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;
.addons-select {
  $button-font-size: 22px;
  $button-height: 65px;

  // .image-wrapper {
  //   height: v-bind("pcselectPictureSize.height");
  //   width: v-bind("pcselectPictureSize.width");
  //   border-radius: 6px;
  //   border: 2px solid $color-light-grey3;
  //   background-color: white;
  //   // Making the image wrapper a flex to center the image in it
  //   display: flex;
  //   flex-direction: column;
  //   // center it vertically if the image is a landscape
  //   justify-content: center;
  //   // center it horizentally if the image is a portrait
  //   align-content: center;

  //   img {
  //     max-height: v-bind("pcselectPictureSize.height");
  //     max-width: v-bind("pcselectPictureSize.width");
  //   }
  // }

  img {
    height: v-bind("pcselectPictureSize.height");
    width: v-bind("pcselectPictureSize.width");
    object-fit: cover;
  }
  .main-pc-timeslot {
    display: flex;
    width: 100%;
    justify-content: center;
    gap: 5px;
  }
  .key {
    line-height: 22px;
    letter-spacing: 0px;
    color: black;
    font-weight: 700;
    font-style: normal;
    font-size: 18px;
    text-align: left;
    margin-bottom: 7px;
  }

  .value {
    line-height: 22px;
    letter-spacing: 0px;
    color: black;
    font-weight: 400;
    font-style: normal;
    font-size: 18px;
    text-align: left;
    margin-bottom: 7px;
  }

  .is-fullwidth {
    width: 100%;
    border: none;
  }

  .price-select-wrapper {
    max-height: 450px;
    overflow-y: auto;
    margin-top: 2rem;
  }
  .pc-grouping {
    font-style: normal;
    text-align: left;
    letter-spacing: 0px;
    font-weight: 400;
    font-size: 27px;
    margin-right: 5px;
    margin-bottom: 10px;
    color: black;
  }
  .main-pc-name-price {
    display: flex;
    flex-direction: column;
  }
  .pc-name {
    font-weight: 700;
    font-size: 32px;
    line-height: 29px;
    color: black;
    margin-left: v-bind("headerMargin");
  }
  .pc-price-name {
    font-weight: 700;
    font-size: 24px;
    line-height: 15px;
    margin-bottom: 32px;
    //color: black;
  }
  // .grid-content {
  .price-selector {
    // padding: 20px 0 0 7px;
    padding: 8px 5px;
    border-radius: 4px;
    min-height: 8vh;
    margin-bottom: 50px;
    color: black;

    .price-name-img-wrapper {
      display: flex;
      gap: 10px;
    }

    .price-name-wrapper {
      font-style: normal;
      text-align: left;
      letter-spacing: 0px;
      font-weight: 400;
      font-size: 16px;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      gap: 1rem;
      margin-left: 1rem;

      .price-name {
        font-weight: 700;
        font-size: 24px;
        line-height: normal;
      }
      .price-description {
        color: rgba(0, 0, 0, 0.5);
        font-size: 0.75rem;
        font-style: normal;
      }
      .see-more {
        color: $color-green;
        cursor: pointer;
      }
    }

    .price-accounting {
      font-family: Arial, sans-serif, Arial;
      font-weight: 400;
      font-style: normal;
      font-size: 22px;
      text-align: center;
      line-height: 27px;
      letter-spacing: 0px;
    }

    .price-value-container {
      border: 1px solid $color-light-grey5;
      border-radius: 4px;
      .editable {
        margin-right: 5px;
        padding: 10px;
        border-radius: 4px 0px 0px 4px;
        width: 70%;
      }

      .edit-price-button {
        margin-right: 5px;
        cursor: pointer;
      }
    }

    .price-count {
      background: white;
      width: 90px;
      min-width: 90px;
      min-height: $button-height;
      font-size: $button-font-size;
      text-align: center;
      border: none;
      outline: none;
      cursor: pointer;
    }
    .input-error {
      text-align: center;
      color: red;
    }
    .price-value {
      text-align: center;
      font-size: 1.1rem;
      font-style: normal;
      font-weight: 700;
    }
    .price-button {
      font-size: $button-font-size;
      height: $button-height;
      min-width: 100px;
      width: 100px;
      background-color: $color-green;
    }
    .transparent {
      opacity: 0.5;
    }
    .max-quantity-reached {
      line-height: 14px;
      font-size: 12px;
      height: 14px;
      margin-bottom: -14px;
    }
  }

  .dialog-footer {
    button {
      width: 25%;
    }
  }

  .price-list-divider {
    margin: 0px 0px 20px;
  }
  .timeslot-actions {
    display: flex;
    justify-content: flex-start;
    text-align: center;
    word-break: break-word;
  }

  .choose-timeslot {
    font-size: 20px;
    font-weight: 700;
    color: $color-green;
    cursor: pointer;
  }
  .chosen-timeslot {
    display: flex;
    align-items: center;
    font-size: 20px;
    white-space: nowrap;
    cursor: pointer;
  }
  .chosen-timeslot-chevron {
    margin-right: 5px;
  }
  .no-timeslots {
    font-size: 18px;
    color: red;
    font-weight: 700;
    white-space: nowrap;
  }

  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  input[type="number"],
  input[type="number"]:hover,
  input[type="number"]:focus {
    -moz-appearance: textfield;
    appearance: none;
  }

  .error-container {
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
  }
  .error-container > p {
    color: red;
    font-size: larger;
  }

  .goback-btn {
    position: absolute;
    top: 1rem;
  }

  .shake-animation {
    animation: shake 0.5s;
  }
  @keyframes shake {
    0% {
      transform: translateX(0);
    }
    25% {
      transform: translateX(-20px);
    }
    50% {
      transform: translateX(20px);
    }
    75% {
      transform: translateX(-20px);
    }
    100% {
      transform: translateX(0);
    }
  }
  .spinner-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.9);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;

    .spinner {
      font-size: 64px;
      color: var(--primary-smeetz);
    }
    .timeslots-loading-container {
      position: flex;
      flex-direction: column;
      font-size: 1.5rem;
      text-align: center;
    }
  }
}
.addons-timeslot-select {
  color: #000;
  font-size: 1rem;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  .select-timeslots-wrapper {
    padding: 25px;
    width: 100%;
  }
  .timeslots-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    width: 100%;
    height: 100%;
  }
  li {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    width: 100%;
    gap: 15px;
  }
  .timeslot {
    display: flex;
    color: #555;
    justify-content: space-evenly;
    align-items: center;
    height: 100px;
    margin-bottom: 2rem;
    border-radius: 10px;
    border: solid 1px;
    cursor: pointer;
  }
  .timeslot:hover {
    background-color: $color-light-grey4;
  }
  .timeslot-start {
    font-size: 25px;
  }
  .timeslot-price-seats {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .seats {
    color: #428891;
  }
  .price {
    font-size: 18px;
  }
  .grouped-hours {
    position: relative;
    margin-left: 24px;
  }
  // .grouped-hours::before {
  //   content: "";
  //   position: absolute;
  //   top: 0;
  //   left: -24px;
  //   width: 2px;
  //   height: 100%;
  //   background-color: $color-light-grey3;
  // }
  .key-grouping {
    font-style: normal;
    text-align: left;
    letter-spacing: 0px;
    font-weight: bold;
    font-size: 32px;
    margin-right: 5px;
    margin-bottom: 20px;
    margin-top: 10px;
    color: black;
  }
}
</style>
